import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import { FileText, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp, Mail, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [deciding, setDeciding] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setSubmissions(await res.json());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleDecision = async (subId, decision) => {
    setDeciding(subId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/${subId}/editor-decision`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ decision }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      fetchSubmissions();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeciding(null);
    }
  };

  // ✅ My decision on a paper
  const myDecision = (sub) => {
    if (!sub.editorDecisions || !user?.id) return null;
    return sub.editorDecisions[user.id] || null;
  };

  const pendingReview = submissions.filter(s => s.status === 'editor_review');
  const alreadyDecided = submissions.filter(s =>
    s.status !== 'editor_review' && s.assignedEditors?.includes(user?.id)
  );

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="section-overline mb-1">Editor Portal</p>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F]">Welcome, {user?.name}</h1>
          <p className="text-[#6B7280] text-sm mt-1">{user?.designation} · {user?.affiliation}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Assigned Papers', value: submissions.length, icon: <FileText size={18} />, bg: 'bg-blue-50', color: 'text-[#1A73E8]' },
            { label: 'Pending Decision', value: pendingReview.length, icon: <Clock size={18} />, bg: 'bg-amber-50', color: 'text-amber-600' },
            { label: 'Approved by Me', value: submissions.filter(s => myDecision(s) === 'approved').length, icon: <CheckCircle size={18} />, bg: 'bg-green-50', color: 'text-green-600' },
            { label: 'Rejected by Me', value: submissions.filter(s => myDecision(s) === 'rejected').length, icon: <XCircle size={18} />, bg: 'bg-red-50', color: 'text-red-500' },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-[#E5E7EB] p-5">
              <div className={`w-9 h-9 ${stat.bg} flex items-center justify-center mb-3 ${stat.color}`}>{stat.icon}</div>
              <p className="text-2xl font-serif font-bold text-[#1F1F1F]">{stat.value}</p>
              <p className="text-xs text-[#6B7280] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* ✅ Papers Awaiting My Decision */}
        <div className="bg-white border border-[#E5E7EB] mb-6">
          <div className="px-5 py-4 border-b border-[#E5E7EB] bg-amber-50">
            <h2 className="font-semibold text-[#1F1F1F] text-base">
              Papers Awaiting Your Decision ({pendingReview.length})
            </h2>
            <p className="text-xs text-[#6B7280] mt-0.5">Papers that need your approval or rejection</p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : pendingReview.length === 0 ? (
            <div className="p-8 text-center text-[#9CA3AF] text-sm">
              No papers currently awaiting your decision.
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {pendingReview.map(sub => {
                const iDecided = myDecision(sub);
                const isExpanded = expanded === sub.id;
                const editorDecisions = sub.editorDecisions || {};
                const assignedEditors = sub.assignedEditors || [];
                const assignedEditorNames = sub.assignedEditorNames || [];

                return (
                  <div key={sub.id} className="p-5">
                    <div className="flex items-start justify-between gap-4">

                      {/* LEFT */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <StatusBadge status={sub.status} />
                          <span className="text-xs text-[#9CA3AF]">{sub.journalTitle}</span>
                          {/* ✅ My decision badge */}
                          {iDecided === 'approved' && (
                            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 font-medium">
                              <CheckCircle size={10} /> You approved
                            </span>
                          )}
                          {iDecided === 'rejected' && (
                            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 font-medium">
                              <XCircle size={10} /> You rejected
                            </span>
                          )}
                        </div>

                        <h3 className="font-semibold text-sm text-[#1F1F1F] leading-snug">{sub.title}</h3>
                        <p className="text-xs text-[#6B7280] mt-1">by {sub.authorName}</p>

                        {/* ✅ Editor approval status (green/red per editor) */}
                        {assignedEditors.length > 1 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {assignedEditors.map((editorId, idx) => {
                              const name = assignedEditorNames[idx] || `Editor ${idx + 1}`;
                              const decision = editorDecisions[editorId];
                              const isMe = editorId === user?.id;
                              return (
                                <span key={editorId} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border font-medium ${
                                  decision === 'approved'
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : decision === 'rejected'
                                    ? 'bg-red-50 text-red-700 border-red-200'
                                    : 'bg-[#F5F7FA] text-[#9CA3AF] border-[#E5E7EB]'
                                }`}>
                                  {decision === 'approved' ? '✓' : decision === 'rejected' ? '✕' : '○'}
                                  {isMe ? 'You' : name}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-4 space-y-3">
                            {sub.abstract && (
                              <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB]">
                                <p className="text-xs font-semibold text-[#6B7280] uppercase mb-1">Abstract</p>
                                <p className="text-sm text-[#374151] leading-relaxed">{sub.abstract}</p>
                              </div>
                            )}
                            {/* ✅ Cover Letter visible to editors */}
                            {sub.coverLetter && (
                              <div className="p-3 bg-blue-50 border border-blue-100">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Mail size={12} className="text-[#1A73E8]" />
                                  <p className="text-xs font-semibold text-[#1A73E8] uppercase">Cover Letter</p>
                                </div>
                                <p className="text-sm text-[#374151] leading-relaxed">{sub.coverLetter}</p>
                              </div>
                            )}
                            {sub.keywords && (
                              <p className="text-xs text-[#9CA3AF]">
                                <span className="font-semibold">Keywords:</span> {
                                  (() => { try { return JSON.parse(sub.keywords).join(', '); } catch { return sub.keywords; } })()
                                }
                              </p>
                            )}
                          </div>
                        )}

                        <button
                          onClick={() => setExpanded(isExpanded ? null : sub.id)}
                          className="text-xs text-[#1A73E8] mt-2 flex items-center gap-1 hover:underline"
                        >
                          {isExpanded ? <><ChevronUp size={12} /> Hide details</> : <><ChevronDown size={12} /> View details & cover letter</>}
                        </button>
                      </div>

                      {/* RIGHT */}
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {sub.fileUrl && (
                          <a
                            href={sub.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="px-4 py-2 text-xs font-semibold border border-[#1A73E8] text-[#1A73E8] hover:bg-[#F5F7FA] text-center transition-colors"
                          >
                            View PDF
                          </a>
                        )}

                        {/* Only show buttons if I haven't decided yet */}
                        {!iDecided ? (
                          <div className="flex gap-2">
                            <button
                              disabled={deciding === sub.id}
                              onClick={() => handleDecision(sub.id, 'approved')}
                              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-60 transition-colors"
                            >
                              <CheckCircle size={12} />
                              {deciding === sub.id ? 'Saving...' : 'Approve'}
                            </button>
                            <button
                              disabled={deciding === sub.id}
                              onClick={() => handleDecision(sub.id, 'rejected')}
                              className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors"
                            >
                              <XCircle size={12} />
                              {deciding === sub.id ? 'Saving...' : 'Reject'}
                            </button>
                          </div>
                        ) : (
                          <div className={`px-4 py-2 text-xs font-semibold text-center border ${
                            iDecided === 'approved'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {iDecided === 'approved' ? '✓ Approved by you' : '✕ Rejected by you'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ✅ Previously Decided Papers */}
        {alreadyDecided.length > 0 && (
          <div className="bg-white border border-[#E5E7EB]">
            <div className="px-5 py-4 border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <h2 className="font-semibold text-[#1F1F1F] text-base">Previously Reviewed ({alreadyDecided.length})</h2>
              <p className="text-xs text-[#6B7280] mt-0.5">Papers you have already reviewed</p>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {alreadyDecided.map(sub => (
                <div key={sub.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1F1F1F] truncate">{sub.title}</p>
                    <p className="text-xs text-[#9CA3AF]">by {sub.authorName} · {sub.journalTitle}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {myDecision(sub) === 'approved' && (
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 font-medium">
                        ✓ You approved
                      </span>
                    )}
                    <StatusBadge status={sub.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
